import { useMutation } from 'convex/react';
import { useCallback, useState } from 'react';
import { api } from '@repo/backend';
import type { Id } from '../../../../packages/backend/convex/_generated/dataModel';
import * as Sentry from '@sentry/react';
import { toUserFriendlyErrorMessage } from '@/lib/errors';

type BidState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'success' }
  | { status: 'error'; message: string };

/**
 * Hook for placing a bid on an auction.
 *
 * Returns a stable `placeBid(auctionId, amount)` function and a `state`
 * object so callers can render optimistic / error / success UI.
 */
export function usePlaceBid() {
  const mutate = useMutation(api.bids.mutations.placeBid);
  const [state, setState] = useState<BidState>({ status: 'idle' });

  const placeBid = useCallback(
    async (auctionId: string, amount: number) => {
      setState({ status: 'pending' });
      try {
        await mutate({ auctionId: auctionId as Id<'auctions'>, amount });
        setState({ status: 'success' });
        // Auto-reset after 3 s so the form is re-usable
        setTimeout(() => setState({ status: 'idle' }), 3_000);
      } catch (err) {
        const message = toUserFriendlyErrorMessage(
          err,
          'We could not place your bid. Please try again.',
        );
        Sentry.captureException(err, {
          extra: { auctionId, amount, message },
        });
        setState({ status: 'error', message });
        throw err;
      }
    },
    [mutate],
  );

  return { placeBid, state };
}
